import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError, type KyInstance } from 'ky'
import {
  AfterPaymentError,
  NativeStoreError,
  subscribe as subscribeCore,
  type ServiceLevelStatus,
  type SubscribeOptions,
  type SubscriptionServiceLevelStatus,
} from './subscribe'
import { restoreSubscription, type RestoreSubscriptionOptions } from './restore'
import type { PaymentPlatform, ProductMetadata } from './types'

/**
 * A classified, framework-agnostic description of why a purchase failed.
 *
 * `classifyPurchaseError` turns a raw thrown error into one of these intents;
 * a per-app `PurchasePresenter` decides how to render each one. `AbortError`
 * is deliberately not part of this union — it is a no-op handled by the caller.
 */
export type PurchaseErrorIntent =
  | { kind: 'payment_timeout'; detail: string }
  | { kind: 'illegal_transaction_restore' }
  | { kind: 'http_error'; status: number }
  | { kind: 'no_internet' }
  | { kind: 'unknown'; code: string }

export function classifyPurchaseError(error: unknown): PurchaseErrorIntent {
  if (error instanceof NativeStoreError) {
    return { kind: 'payment_timeout', detail: error.message }
  }
  if (error instanceof AfterPaymentError) {
    return { kind: 'illegal_transaction_restore' }
  }
  if (isHTTPError(error)) {
    return { kind: 'http_error', status: error.response.status }
  }
  if (isTimeoutError(error) || isNetworkError(error as Error)) {
    return { kind: 'no_internet' }
  }
  const code =
    error instanceof Error ? error.name + error.message : String(error)
  return { kind: 'unknown', code }
}

/**
 * The handful of facts every app needs from a completed purchase to drive its
 * own success side-effects (analytics labels, reminder dialogs, store sets).
 * Derived purely from the polled service-level status and the purchased product.
 */
export interface SuccessFacts {
  /** Whether the poll resolved to an active subscription (vs. null/timeout). */
  isSubscription: boolean
  /** Whether the resolved subscription is currently in its trial period. */
  isTrialSuccess: boolean
  /** The subscription's expiry, when known — used to schedule reminders. */
  expiresAt?: string
  /** The purchased product's store SKU. */
  sku: string
}

export function deriveSuccessFacts(
  status: SubscriptionServiceLevelStatus | null,
  product: ProductMetadata,
): SuccessFacts {
  const isSubscription = status?.service_level === 'subscription'
  return {
    isSubscription,
    isTrialSuccess: isSubscription && status.subscription.is_in_trial,
    expiresAt: isSubscription ? status.subscription.expires_at : undefined,
    sku: product.sku,
  }
}

/** An app's native-bridge bindings, as the flow needs them. */
export interface SubscribeFlowNativeBindings<Product extends ProductMetadata> {
  purchase: SubscribeOptions<Product>['nativePurchase']
  // Stricter of the two consumers (restore requires Promise<void>); also
  // satisfies subscribeCore, whose closeTransaction accepts void | Promise<void>.
  closeTransaction: RestoreSubscriptionOptions['closeTransaction']
  getAllUnclosedTransactions: RestoreSubscriptionOptions['getAllUnclosedTransactions']
  getAllEntitlements: RestoreSubscriptionOptions['getAllEntitlements']
  // Same shape subscribeCore expects, so it can be threaded straight through.
  logEvent: NonNullable<SubscribeOptions<Product>['logEvent']>
  refreshServiceLevel: () => void
}

/** Where the flow reports step failures (e.g. PostHog). Optional per app. */
export interface SubscribeFlowTelemetry {
  captureException: (
    error: unknown,
    context: {
      step: string
      purchase_id?: number
      transaction_id?: string
      platform: PaymentPlatform
      product_sku: string
    },
  ) => void
}

export interface SubscribeFlowConfig<Product extends ProductMetadata> {
  api: { service: KyInstance; receipt: KyInstance }
  platform: PaymentPlatform
  native: SubscribeFlowNativeBindings<Product>
  /** When omitted, step failures are not reported (and not renamed). */
  telemetry?: SubscribeFlowTelemetry
  /** Resolves the authenticated user id (any app-specific auth side-effects). */
  getUserId: () => Promise<number>
  getReceiptSystemAccountId: () => string | null
  /** When it returns true, a debug AfterPaymentError is simulated post-payment. */
  getDebugSimulate?: () => boolean
  /**
   * The currently-active subscription's expiry, if any. When provided, both the
   * purchase poll and a restore wait for the expiry to change before resolving
   * (so a renewal is not mistaken for the prior subscription), and China
   * Android interrupted-purchase recovery requires the expiry to advance.
   *
   * Omitting this getter is a guarantee that this flow cannot be opened by an
   * already-subscribed user.
   */
  getCurrentSubscriptionExpiresAt?: () => string | null | undefined
}

export interface SubscribeFlow<Product extends ProductMetadata> {
  /** Auth + receipt log + the 6-step subscribeCore orchestration + step telemetry. */
  run: (product: Product) => Promise<SubscriptionServiceLevelStatus | null>
  /** Common success analytics + refreshServiceLevel; returns the derived facts. */
  handleSuccess: (
    status: SubscriptionServiceLevelStatus | null,
    product: Product,
  ) => SuccessFacts
  /** Wraps restoreSubscription with the app's native bindings. */
  restore: () => Promise<SubscriptionServiceLevelStatus | null>
}

/**
 * The single seam each app implements: how it renders a finished purchase and
 * each classified failure. The flow owns the framework-agnostic orchestration;
 * the presenter owns the app-specific UI (dialog store, message catalog) and
 * the success side-effects that vary per app (reminder dialogs, store sets).
 *
 * A consumer wires the two together in a ~15-line framework-native shell:
 *
 * ```ts
 * const flow = createSubscribeFlow(config)
 * const presenter: PurchasePresenter<Product> = { ... }
 *
 * useMutation({
 *   mutationFn: (product) => flow.run(product),
 *   onSuccess: (status, product) =>
 *     presenter.success(flow.handleSuccess(status, product)),
 *   onError: (error) => {
 *     if (error instanceof AbortError) return // a no-op, kept caller-side
 *     presenter.error(classifyPurchaseError(error), { error, flow })
 *   },
 * })
 * ```
 *
 * `AbortError` is deliberately not modelled as a `PurchaseErrorIntent`: it is a
 * no-op the shell short-circuits before classifying. For the
 * `illegal_transaction_restore` intent the presenter drives its restore dialog
 * around `flow.restore()`; `context.error` is provided so the presenter can
 * still capture the raw exception (e.g. the `unknown` intent) and inspect
 * restore-error detail.
 */
export interface PurchasePresenter<
  Product extends ProductMetadata = ProductMetadata,
> {
  success: (facts: SuccessFacts) => void | Promise<void>
  error: (
    intent: PurchaseErrorIntent,
    context: { error: unknown; flow: SubscribeFlow<Product> },
  ) => void | Promise<void>
}

export function createSubscribeFlow<Product extends ProductMetadata>(
  config: SubscribeFlowConfig<Product>,
): SubscribeFlow<Product> {
  const {
    api,
    platform,
    native,
    telemetry,
    getUserId,
    getReceiptSystemAccountId,
    getDebugSimulate,
    getCurrentSubscriptionExpiresAt,
  } = config

  const run: SubscribeFlow<Product>['run'] = async (product) => {
    const userId = await getUserId()
    const receiptSystemAccountId = getReceiptSystemAccountId()
    void native.logEvent({
      event_name: 'receipt_system_account_id_status',
      event_params: { is_present: String(!!receiptSystemAccountId) },
    })

    const currentSubscriptionExpiresAt = getCurrentSubscriptionExpiresAt?.()
    const shouldResolve = getCurrentSubscriptionExpiresAt
      ? buildShouldResolve(currentSubscriptionExpiresAt)
      : undefined
    const onStepError = telemetry
      ? buildStepTelemetry(telemetry, platform, product.sku)
      : undefined

    return subscribeCore({
      api,
      platform,
      userId,
      product,
      nativePurchase: native.purchase,
      closeTransaction: native.closeTransaction,
      receiptSystemAccountId,
      shouldResolve,
      currentSubscriptionExpiresAt,
      debugSimulateAfterPaymentError: getDebugSimulate?.(),
      logEvent: native.logEvent,
      onStepError,
    })
  }

  const handleSuccess: SubscribeFlow<Product>['handleSuccess'] = (
    status,
    product,
  ) => {
    void native.logEvent({
      event_name: 'product_sku_purchased_success',
      event_params: { sku_id: product.sku },
    })
    const facts = deriveSuccessFacts(status, product)
    void native.logEvent({
      event_name: facts.isTrialSuccess
        ? 'plus_trial_success'
        : 'plus_subscribe_success',
      event_params: { sku_id: product.sku },
    })
    native.refreshServiceLevel()
    return facts
  }

  const restore: SubscribeFlow<Product>['restore'] = async () => {
    const userId = await getUserId()
    return restoreSubscription({
      api,
      userId,
      platform,
      getAllUnclosedTransactions: native.getAllUnclosedTransactions,
      getAllEntitlements: native.getAllEntitlements,
      closeTransaction: native.closeTransaction,
      currentSubscriptionExpiresAt: getCurrentSubscriptionExpiresAt?.(),
    })
  }

  return { run, handleSuccess, restore }
}

function buildShouldResolve(
  currentSubscriptionExpiresAt: string | null | undefined,
): NonNullable<SubscribeOptions<ProductMetadata>['shouldResolve']> {
  return (
    status: ServiceLevelStatus,
  ): status is SubscriptionServiceLevelStatus =>
    status.service_level === 'subscription' &&
    status.subscription.expires_at !== currentSubscriptionExpiresAt
}

function buildStepTelemetry(
  telemetry: SubscribeFlowTelemetry,
  platform: PaymentPlatform,
  productSku: string,
): NonNullable<SubscribeOptions<ProductMetadata>['onStepError']> {
  return (error, context) => {
    if (
      context.step === 'native_purchase' &&
      error instanceof NativeStoreError &&
      error.message === 'interrupted'
    ) {
      return
    }
    if (error instanceof Error) {
      error.name = 'SubscriptionError'
    }
    telemetry.captureException(error, {
      step: context.step,
      purchase_id: context.purchaseId,
      transaction_id: context.transactionId,
      platform,
      product_sku: productSku,
    })
  }
}
